import React from 'react'
import PropTypes from 'prop-types'
import RemoveIcon from '@material-ui/icons/RemoveCircle'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import { Field } from 'react-formality'
import { Button, TextField, MenuItem, Select, InputLabel, FormControl, CircularProgress } from '@material-ui/core'
import classNames from 'classnames'
import Collection from './components/Collection'
import linkTypes from './enums/linkTypes'
import SearchBox from './components/SearchBox'
import searchTypes from './enums/searchTypes'
import voteTypes from './enums/voteTypes'
import connectPropTypes from './utilities/connectPropTypes'
import preventableBinder from './utilities/preventableBinder'
import getNormalisedParameters from './utilities/getNormalisedParameters'
import Thing from './utilities/Data/Thing'
import getDecodedURIComponent from './utilities/getDecodedURIComponent'
import mergeThings from './utilities/Data/mergeThings'
import Item from './utilities/Data/Item'
import ExpandableSection from './components/ExpandableSection'
import Form from './components/Form'
import AutoComplete from './components/AutoComplete'
import itemTypes from './enums/itemTypes'
import messageTypes from './enums/messageTypes'
import errorTypes from './enums/errorTypes'
import { firestore } from './utilities/firebase'
import Colour from './utilities/Data/Colour'
import styles from './KitEditSearch.icss'

const ownItemsLimit = 5

const hasBeenUsed = (item, kit, isSuggestion, kitSuggestions) => {
	const kitItems = isSuggestion ? kitSuggestions : kit && kit.items
	const hasItems = kitItems && kitItems.length > 0
	if (!hasItems) {
		return false
	}

	return kitItems.some(kitItem => Item.isSameItem(item, kitItem))
}

// case insensitive string comparison
const stringIncludes = (inString, value) => {
	if (!inString) {
		return false
	}
	if (!value) {
		return true
	}
	return inString.toLowerCase().includes(value.toLowerCase())
}

// any of the array items match
const arrayIncludes = (inArray, value) => {
	if (!Array.isArray(inArray) || !inArray.length) {
		return false
	}
	return stringIncludes(inArray.reduce((a, c) => `${a} ${c}`, ''), value)
}

// the term is found in one of the item's fields
const termFoundInItemPredicate = item => term => (
	stringIncludes(item.brandName, term) ||
	stringIncludes(item.itemName, term) ||
	stringIncludes(item.colourName, term) ||
	arrayIncludes(item.category, term) ||
	stringIncludes(item.description, term)
)

// all of the terms (words in the query) are found in the item
const itemMatchesQueryPredicate = query => (item) => {
	const terms = query.split(' ').filter(s => !!s)
	if (!terms) {
		return true
	}
	return terms.every(termFoundInItemPredicate(item))
}

// filter an array of items
// keep items that have all of the terms in the query in some field
const filterItemsByQuery = (items, query) => items.filter(itemMatchesQueryPredicate(query))

class KitEditSearch extends React.Component {
	constructor(props) {
		super(props)

		const parsedSearch = getNormalisedParameters(this.props.location.search)

		this.form = React.createRef()
		this.state = {
			kitId: getDecodedURIComponent(parsedSearch.kitid || props.match.params.kitId),
			addedItems: [],
			brandName: '',
			itemName: '',
			itemType: '',
			addingInProgress: false
		}

		this.selectAlgoliaItemHandler = preventableBinder(this.selectAlgoliaItemHandler.bind(this), 'itemForKit')
		this.selectItemHandler = preventableBinder(this.selectItemHandler.bind(this), 'itemForKit')
		this.addItemHandler = this.addItemHandler.bind(this)
		this.bindHandleDeleteButtonClick = preventableBinder(this.bindHandleDeleteButtonClick.bind(this), 'itemForKit')
		this.renderAddItemForm = this.renderAddItemForm.bind(this)
	}

	componentDidMount() {
		//	Load in the kit if it isn't present on props
		if (this.props.isSuggestion) {
			if (!this.props.kitSuggestions) {
				if (this.state.kitId) {
					this.props.kitSuggestionsFetch({ kitId: this.state.kitId })
				} else {
					// this should not happen
					this.props.appRedirect('/')
					return
				}
			}
		} else if (!this.props.kitDraft) {
			if (this.state.kitId) {
				this.props.kitDraftFetch({ kitId: this.state.kitId })
			} else {
				this.props.kitDraftCreate()
			}
		}

		this.props.appHeading({ text: this.props.isSuggestion ? this.context.translate('Search for your suggestion') : this.context.translate('Add items to your kit') })
		this.props.appBottomBarValue(linkTypes.ADD)
		this.props.appShowBottomBar(false)
		this.props.appShowBottomNavBar(true)
		this.props.appBottomNavBarHandleClick(() => {
			if (this.props.isSuggestion) {
				this.props.kitSuggestionSet({
					kitSuggestion: this.props.addedKitSuggestions.map(aks => ({
						userName: this.props.user.userProfile.userName,
						colourName: aks.colourName,
						itemKey: aks.itemKey,
						kitId: this.state.kitId,
						voteType: voteTypes.UP.type
					}))
				})
				this.props.appRedirect(`/kit/${this.state.kitId}`)
			} else if (this.props.isPostkit) {
				this.props.appRedirect(`/postkit${this.state.kitId ? `/${this.state.kitId}` : ''}`)
			} else {
				this.props.appRedirect(`/kit${this.state.kitId ? `/${this.state.kitId}` : ''}`)
			}
		})
		this.props.appBottomNavBarLabelNext(this.context.translate('DONE'))
		// Load current users items
		if (!this.props.user.userProfileItemsOwned) {
			this.props.userProfileFetchItems()
		}
	}

	componentDidUpdate() {
		const pageHasLoaded = !this.props.searchLoading
		if (this.props.pageHasLoaded !== pageHasLoaded) {
			this.props.appPageLoaded(pageHasLoaded)
		}
	}

	bindHandleDeleteButtonClick(itemKey, colourName) {
		return () => (this.props.isSuggestion ? this.props.kitSuggestionDelete({ uid: this.props.user.uid, kitId: this.state.kitId, itemKey, colourName }) : this.props.kitDraftItemDelete({ itemKey, colourName }))
	}

	selectAlgoliaItemHandler(aItem) {
		if (this.props.isSuggestion) {
			this.props.kitSuggestItem({ aItem })
		} else {
			this.props.kitDraftSelectItem({ aItem })
		}
	}

	selectItemHandler(item) {
		if (this.props.isSuggestion) {
			this.props.kitSuggestItem({ item })
		} else {
			this.props.kitDraftSelectItem({ item })
		}
	}

	addItemHandler(item) {
		if (this.props.isSuggestion) {
			this.props.kitSuggestItem({ item })
		} else {
			this.props.kitDraftSelectItem({ item })
		}
	}

	handleInvalidFormSubmit() {
		this.props.appMessage(messageTypes.ERROR, this.context.translate('Item invalid, please check all the fields'), errorTypes.LOCALISEDERROR)
	}

	handleValidFormSubmit({ values, store }) { // eslint-disable-line class-methods-use-this
		this.setState({ addingInProgress: true })

		const itemDraft = { item: { ...values, isPlaceholder: true } }

		const done = (item) => {
			// it's a placeholder item, set the default colour name
			item.colourName = Colour.defaultColourName // eslint-disable-line no-param-reassign
			if (this.props.isSuggestion) {
				this.props.kitSuggestItem({ item })
			} else {
				this.props.kitDraftSelectItem({ item })
			}
			// reset the itemName entry, to avoid duplicates
			store.setBinder('itemName')(undefined, '')
			this.setState(state => ({ addingInProgress: false, addedItems: [...state.addedItems, item] }))
		}

		const fail = () => {
			this.props.appMessage(messageTypes.ERROR, this.context.translate('Item add failed, sorry'), errorTypes.LOCALISEDERROR)
			this.setState({ addingInProgress: false })
		}

		this.props.itemVersionAdd({ itemDraft }, {
			success: ({ itemKey }) => {
				const ref = firestore.collection('items').doc(itemKey)
				let unsubscribeObserver
				const timeoutRedirect = setTimeout(() => {
					unsubscribeObserver()
					fail()
				}, 10000)
				unsubscribeObserver = ref.onSnapshot((docSnap) => {
					if (docSnap.exists) {
						clearTimeout(timeoutRedirect)
						unsubscribeObserver()
						done(docSnap.data())
					}
				}, (err) => {
					console.error(`[ERROR]: ${err}`)
				})
			},
			fail
		})
	}

	renderAddItemForm() {
		return (
			<Form
				onSubmit={args => (args.isValid ? this.handleValidFormSubmit(args) : this.handleInvalidFormSubmit(args))}
				ref={this.form}
				initialValues={{
					brandName: this.state.brandName,
					itemName: this.state.itemName,
					itemType: this.state.itemType
				}}
				onChange={({ values }) => this.setState({ ...values })}
			>
				{props => (
					<>
						<section className={styles.fieldSurround}>
							<Field
								component={AutoComplete}
								name="brandName"
								value={props.values.brandName}
								label={this.context.translate('Brand')}
								placeholder={this.context.translate('What brand is it?')}
								suggestions={this.props.brandSuggestions}
								suggestionKey={suggestion => suggestion.brandKey}
								suggestionValue={suggestion => suggestion.brandName}
								suggestionContent={suggestion => suggestion.brandName}
								onInputValueChange={entry => this.props.brandSuggestionsFetch({ query: entry })}
								onSuggestionSelected={(suggestion) => {
									props.store.setBinder('brandName')(undefined, suggestion.brandName)
									props.store.setBinder('brandKey')(undefined, suggestion.brandKey)
								}}
								validate={{
									presence: true
								}}
							/>
						</section>
						<section className={styles.fieldSurround}>
							<Field
								supportsHelperText={false}
								component={FormControl}
								name="itemType"
								validate={{
									presence: true
								}}
							>
								<InputLabel>{this.context.translate('Item type')}</InputLabel>
								<Select
									value={props.values.itemType}
									onChange={props.store.setBinder('itemType')}
								>
									{Object.keys(itemTypes).sort().map(it => <MenuItem key={it} value={it}>{itemTypes[it].name}</MenuItem>)}
								</Select>
							</Field>
						</section>
						<section className={styles.fieldSurround}>
							<Field
								component={TextField}
								name="itemName"
								value={props.values.itemName}
								label={this.context.translate('Name')}
								validate={{
									presence: true
								}}
							/>
						</section>
					</>
				)}
			</Form>
		)
	}

	render() {
		const renderAfterItem = (renderProps) => {
			const item = renderProps.item
			const displayMode = renderProps.displayMode

			return (
				<React.Fragment>
					{hasBeenUsed(item, this.props.kitDraft, this.props.isSuggestion, this.props.kitSuggestions) && (
						<button
							key={`button${item.objectID}`}
							type="button"
							className={styles.addedToKit}
							onClick={this.bindHandleDeleteButtonClick(item.itemKey, Item.getColourName(item))}
						>
							<span>
								<RemoveIcon className={styles.removeItemFromKit} />
							</span>
							<span className={styles[`added${displayMode}`]}>
								{this.props.isSuggestion ? this.context.translate('Suggested') : this.context.translate('Added to kit')}
							</span>
						</button>
					)}
				</React.Fragment>
			)
		}

		const { userProfileItemsOwned, userProfileItemsWanted } = this.props.user
		const mergeAndSortItems = (...thingArrays) => Thing.sort(Thing.deduplicate(mergeThings(...thingArrays)))
		const loadedOwnItems = userProfileItemsOwned && userProfileItemsWanted
		// all the user's items
		const allOwnItems = (loadedOwnItems && mergeAndSortItems(userProfileItemsOwned, userProfileItemsWanted)) || []
		const hasSomeOwnItems = !!allOwnItems.length
		// filtered by the current query
		let filteredOwnItems = allOwnItems
		if (this.props.searchQuery) {
			filteredOwnItems = filterItemsByQuery(allOwnItems, this.props.searchQuery)
		}
		// limited by the number to be shown (5 with search results, all otherwise or after 'more' selected)
		const ownItems = (this.state.showMoreOwnItems || !this.props.searchQuery) ? filteredOwnItems : filteredOwnItems.slice(0, ownItemsLimit)
		const hasLimitedOwnItems = ownItems.length < filteredOwnItems.length

		const addButtonText = this.props.isSuggestion ? this.context.translate('Suggest') : this.context.translate('Add to kit')

		return (
			<section>
				{(this.props.kitDraft || (this.props.isSuggestion && this.props.kitSuggestions)) && (
					<>
						<div className={styles.inputLine}>
							<div key="searchInput" className={styles.searchInput}>
								<SearchBox
									hintText={this.props.isSuggestion ? this.context.translate('Find items to suggest') : this.context.translate('Add more items')}
									searchOnMount={false}
									autoFocus
									searchTypeOverride={searchTypes.ITEM.type}
								/>
							</div>
						</div>

						{hasSomeOwnItems && (
							<ExpandableSection title={this.context.translate('Your items')} defaultExpanded>
								<Collection
									classes={{ itemRoot: styles.itemRoot }}
									isLoading={!loadedOwnItems}
									itemDescription={Thing.description}
									itemHref={Thing.href}
									itemImageContent={Thing.images}
									itemKey={Thing.key}
									itemSubtitle={Thing.subtitle}
									itemTitle={Thing.title}
									itemOnSelect={this.selectItemHandler}
									items={ownItems}
									renderAfterItem={renderAfterItem}
									renderWhenNoItems={() => (<div className={styles.nomatches}>{this.context.translate('You have no matching items')}</div>)}
								/>
								{hasLimitedOwnItems && (
									<Button variant="text" onClick={() => this.setState({ showMoreOwnItems: true })} className={styles.moreButton}>
										{this.context.translate('More +')}
									</Button>
								)}
							</ExpandableSection>
						)}

						{this.props.searchQuery && (
							<section className={styles.searchResultsSection}>
								<Collection
									classes={{ itemRoot: styles.itemRoot }}
									isLoading={this.props.searchLoading}
									itemDescription={Thing.description}
									itemHref={Thing.href}
									itemImageContent={Thing.images}
									itemKey={Thing.key}
									itemSubtitle={Thing.subtitle}
									itemTitle={Thing.title}
									itemOnSelect={this.selectAlgoliaItemHandler}
									items={this.props.searchItemResults}
									renderAfterItem={renderAfterItem}
									showResultsSeparator
									renderWhenNoItems={() => (<div className={styles.nomatches}>{this.context.translate('No items found')}</div>)}
								/>
							</section>
						)}

						{!!this.state.addedItems.length && (
							<section className={styles.searchResultsSection}>
								<Collection
									classes={{ itemRoot: styles.itemRoot }}
									itemDescription={Thing.description}
									itemHref={Thing.href}
									itemImageContent={Thing.images}
									itemKey={Thing.key}
									itemSubtitle={Thing.subtitle}
									itemTitle={Thing.title}
									itemOnSelect={this.selectItemHandler}
									items={this.state.addedItems}
									renderAfterItem={renderAfterItem}
								/>
							</section>
						)}

						{this.props.searchQuery && !this.props.searchLoading && (
							<section className={styles.searchResultsSection}>
								<Card className={styles.addKitCard}>
									<CardContent className={styles.addKitCardContent}>
										<div>
											{this.context.translate('Didn’t find what you were looking for? Tell us what it was:')}
											{this.renderAddItemForm()}
											<Button variant="contained" classes={{ root: classNames(styles.addButton, { [styles.selected]: this.state.own }) }} onClick={() => this.form.current.submit()}>
												{this.state.addingInProgress ? <CircularProgress size={24} thickness={4} /> : addButtonText}
											</Button>
										</div>
									</CardContent>
								</Card>
							</section>
						)}
					</>
				)}
			</section>
		)
	}
}

KitEditSearch.contextTypes = {
	translate: PropTypes.func
}

KitEditSearch.propTypes = {
	isSuggestion: PropTypes.bool,
	isPostkit: PropTypes.bool,
	location: PropTypes.shape({
		search: PropTypes.string
	}).isRequired,
	match: PropTypes.shape({
		params: PropTypes.object.isRequired
	}).isRequired,
	appHeading: PropTypes.func.isRequired,
	appBottomBarValue: PropTypes.func.isRequired,
	appRedirect: PropTypes.func.isRequired,
	appShowBottomBar: PropTypes.func.isRequired,
	appShowBottomNavBar: PropTypes.func.isRequired,
	appBottomNavBarHandleClick: PropTypes.func.isRequired,
	appBottomNavBarLabelNext: PropTypes.func.isRequired,
	appPageLoaded: PropTypes.func.isRequired,
	appMessage: PropTypes.func.isRequired,
	pageHasLoaded: PropTypes.bool.isRequired,
	itemVersionAdd: PropTypes.func.isRequired,
	kitDraftFetch: PropTypes.func.isRequired,
	kitDraftCreate: PropTypes.func.isRequired,
	kitSuggestionsFetch: PropTypes.func.isRequired,
	kitDraft: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitDraftItemDelete: PropTypes.func.isRequired,
	kitSuggestionDelete: PropTypes.func.isRequired,
	kitDraftSelectItem: PropTypes.func.isRequired,
	kitSuggestItem: PropTypes.func.isRequired,
	kitSuggestionSet: PropTypes.func.isRequired,
	kitSuggestions: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	brandSuggestions: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	brandSuggestionsFetch: PropTypes.func.isRequired,
	user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
	addedKitSuggestions: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	searchLoading: PropTypes.bool,
	searchQuery: PropTypes.string,
	searchItemResults: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfileFetchItems: PropTypes.func.isRequired

}

KitEditSearch.defaultProps = {
	isSuggestion: false,
	isPostkit: false,
	kitDraft: undefined,
	searchLoading: undefined,
	searchQuery: undefined,
	kitSuggestions: undefined,
	addedKitSuggestions: [],
	searchItemResults: [],
	brandSuggestions: undefined
}

export const testing = {
	KitEditSearch
}

export default connectPropTypes(KitEditSearch)



// WEBPACK FOOTER //
// ./src/KitEditSearch.jsx